import { LineChart, Zap, Globe, Radio } from 'lucide-react';
import { PageHeader, GettingStarted, QuickLinksGrid } from '../components/landing';
import { PLATFORM_URL } from '../config/platformUrls';
import { useContext } from 'react';
import { AuthContext } from '../components/auth/AuthProvider';
import { createCrossOriginAuthUrl } from '../utils/authSync';

export default function UserLanding() {
  const { session } = useContext(AuthContext);

  const handlePlatformLink = () => {
    const authUrl = createCrossOriginAuthUrl(PLATFORM_URL, session);
    window.open(authUrl, '_blank');
  };

  const quickLinks = [
    {
      title: 'Weather Forecasts',
      description: 'Access high-resolution weather forecasts and predictions',
      href: '/forecast',
      icon: LineChart,
    },
    {
      title: 'Energy Market Data',
      description: 'Track energy market aggregates and pricing insights',
      href: '/market-aggregates',
      icon: Zap,
    },
    {
      title: 'Weather Stations',
      description: 'Explore weather station data and compare with forecasts',
      href: '/stations',
      icon: Radio,
    },
    {
      title: 'Interactive Map',
      description: 'Visualize weather data on an interactive global map',
      href: '/map',
      icon: Globe,
    },
  ];

  const gettingStartedSteps = [
    {
      title: 'Explore Forecasts',
      description: 'Start with weather forecasts for your region',
    },
    {
      title: 'Check Market Data',
      description: 'Monitor energy market trends and pricing',
    },
    {
      title: 'Analyze Performance',
      description: 'Review model performance with live benchmarking',
    },
  ];

  const isLinkAccessible = (link: any) => {
    // Since ProtectedRoute handles access control, only check for teaser links
    return !link.teaser;
  };

  const isLinkVisible = () => {
    // All links are visible since access control is handled by ProtectedRoute
    return true;
  };

  return (
    <div className="max-w-6xl mx-auto p-6 sm:p-8 lg:pl-12">
      <PageHeader
        title="Welcome to Jua"
        description="Your comprehensive platform for weather intelligence and energy market insights. Access cutting-edge forecasts, market data, and analytics to make informed decisions in the energy sector."
      />

      <GettingStarted
        title="Getting Started"
        description="New to the platform? Here's how to get the most out of your weather intelligence dashboard:"
        steps={gettingStartedSteps}
      />

      <QuickLinksGrid
        title="Quick Access"
        links={quickLinks}
        isLinkAccessible={isLinkAccessible}
        isLinkVisible={isLinkVisible}
      />

      <div className="mt-12 p-6 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-2">Looking for Developer Tools?</h3>
        <p className="text-gray-600 mb-4">
          Access our developer platform for API documentation, technical resources, and advanced
          features.
        </p>
        <button
          onClick={handlePlatformLink}
          className="inline-flex items-center text-[#E34290] hover:text-[#C93B7D] font-medium"
        >
          Visit Developer Platform
          <svg
            className="ml-2 w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
